/**
 * Product Media Desktop Grid Handler
 * 
 * This script disables Flickity on desktop screens and displays product media
 * in a 2-column grid layout. It ensures all images are visible and prevents
 * the carousel from interfering with the grid display.
 */

class ProductMediaGridHandler {
  constructor() {
    this.desktopBreakpoint = 1000;
    this.desktopMediaQuery = window.matchMedia(`screen and (min-width: ${this.desktopBreakpoint}px)`);
    this.isDesktop = window.innerWidth >= this.desktopBreakpoint;
    this.carousels = new Map(); // Track carousel state

    this.init();
    this.setupResizeListener();
  }

  init() {
    // Find all flickity-carousel elements that are part of product media
    document.querySelectorAll('flickity-carousel').forEach((carousel) => {
      const productMedia = carousel.closest('product-media');
      if (productMedia) {
        this.handleCarousel(carousel, productMedia);
      }
    });
  }

  handleCarousel(carousel, productMedia) {
    const carouselId = carousel.id || Math.random().toString(36).substr(2, 9);
    carousel.id = carouselId;
    
    if (!this.carousels.has(carouselId)) {
      this.carousels.set(carouselId, {
        carousel,
        productMedia,
        isGridMode: false,
        flickityInstance: null
      });
    }

    if (this.isDesktop) {
      this.convertToGrid(carousel);
    } else {
      this.convertToCarousel(carousel);
    }
  }

  convertToGrid(carousel) {
    const carouselData = this.carousels.get(carousel.id);
    
    // Prevent double conversion
    if (carouselData.isGridMode) {
      return;
    }

    // Destroy flickity if it exists
    this.destroyFlickity(carousel);

    // Add grid class
    carousel.classList.add('product__media-list--grid-desktop');
    carousel.classList.remove('flickity-enabled');
    
    // Remove the flickity-viewport and flickity-slider wrappers that might exist
    const viewport = carousel.querySelector('.flickity-viewport');
    if (viewport) {
      // Move all children out of the viewport back to the carousel
      while (viewport.firstChild) {
        if (viewport.firstChild.classList && viewport.firstChild.classList.contains('flickity-slider')) {
          // This is the slider, move its children
          const slider = viewport.firstChild;
          while (slider.firstChild) {
            carousel.appendChild(slider.firstChild);
          }
        } else {
          carousel.appendChild(viewport.firstChild);
        }
      }
      viewport.remove();
    }

    // Ensure all items are visible and positioned correctly
    carousel.querySelectorAll('.product__media-item').forEach((item) => {
      item.style.position = 'relative';
      item.style.left = 'auto';
      item.style.transform = 'none';
      item.style.display = item.classList.contains('is-filtered') ? 'none' : 'block';
    });

    // Add grid styles directly
    // carousel.style.display = 'grid';
    // carousel.style.gridTemplateColumns = 'repeat(2, 1fr)';
    // carousel.style.gap = '16px';

    carouselData.isGridMode = true;
  }

  convertToCarousel(carousel) {
    const carouselData = this.carousels.get(carousel.id);
    
    // Prevent double conversion
    if (!carouselData.isGridMode) {
      return;
    }

    // Remove grid class and inline styles
    carousel.classList.remove('product__media-list--grid-desktop');
    carousel.style.display = '';
    carousel.style.gridTemplateColumns = '';
    carousel.style.gap = '';

    // Reset item styles
    carousel.querySelectorAll('.product__media-item').forEach((item) => {
      item.style.position = '';
      item.style.left = '';
      item.style.transform = '';
      item.style.display = '';
    });

    // Keep one item visible before Flickity re-initializes; otherwise
    // base CSS can hide all slides when no ".is-selected" exists.
    const selectedItem = carousel.querySelector('.product__media-item.is-selected:not(.is-filtered)');
    if (!selectedItem) {
      const firstVisibleItem = carousel.querySelector('.product__media-item:not(.is-filtered)');
      if (firstVisibleItem) {
        carousel.querySelectorAll('.product__media-item.is-selected').forEach((item) => item.classList.remove('is-selected'));
        firstVisibleItem.classList.add('is-selected');
      }
    }

    // Reinitialize Flickity after grid styles have been removed so it can
    // measure the mobile slide widths correctly when resizing down.
    requestAnimationFrame(() => this.initializeFlickity(carousel));

    carouselData.isGridMode = false;
  }

  destroyFlickity(carousel) {
    if (typeof carousel._destroyFlickity === 'function') {
      carousel._destroyFlickity();
      return;
    }
    if (carousel.flickity) {
      try {
        const flickityPromise = carousel.flickity;
        if (flickityPromise instanceof Promise) {
          flickityPromise.then((instance) => {
            if (instance && typeof instance.destroy === 'function' && instance.isActive) {
              instance.destroy();
            }
          }).catch(() => {
            // Ignore errors during destruction
          });
        } else if (typeof flickityPromise.destroy === 'function' && flickityPromise.isActive) {
          flickityPromise.destroy();
        }
      } catch (e) {
        // Silently fail
      }
    }
    delete carousel.flickity;
  }

  initializeFlickity(carousel) {
    // Trigger the custom element's Flickity initialization
    if (carousel._syncFlickityForViewport && typeof carousel._syncFlickityForViewport === 'function') {
      carousel._syncFlickityForViewport().then(async () => {
        const flickityInstance = await carousel.flickityInstance;
        flickityInstance?.reloadCells();
        flickityInstance?.resize();
        flickityInstance?.reposition();
      });
    } else if (carousel._createFlickity && typeof carousel._createFlickity === 'function') {
      carousel._createFlickity().then(async () => {
        const flickityInstance = await carousel.flickityInstance;
        flickityInstance?.reloadCells();
        flickityInstance?.resize();
        flickityInstance?.reposition();
      });
    }
  }

  setupResizeListener() {
    const handleBreakpointChange = () => {
      const wasDesktop = this.isDesktop;
      this.isDesktop = this.desktopMediaQuery.matches;

      if (wasDesktop !== this.isDesktop) {
        this.carousels.forEach((data) => {
          if (this.isDesktop) {
            this.convertToGrid(data.carousel);
          } else {
            this.convertToCarousel(data.carousel);
          }
        });
      }
    };

    if (this.desktopMediaQuery.addEventListener) {
      this.desktopMediaQuery.addEventListener('change', handleBreakpointChange);
    } else {
      this.desktopMediaQuery.addListener(handleBreakpointChange);
    }

    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const wasDesktop = this.isDesktop;
        this.isDesktop = window.innerWidth >= this.desktopBreakpoint;

        // If screen size crossed the breakpoint, update carousels
        if (wasDesktop !== this.isDesktop) {
          this.carousels.forEach((data, carouselId) => {
            if (this.isDesktop) {
              this.convertToGrid(data.carousel);
            } else {
              this.convertToCarousel(data.carousel);
            }
          });
        }
      }, 250);
    });
  }
}

function getOrCreateProductMediaGridHandler() {
  if (!window.productMediaGridHandler) {
    window.productMediaGridHandler = new ProductMediaGridHandler();
  }
  return window.productMediaGridHandler;
}

// Initialize once when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    getOrCreateProductMediaGridHandler();
  }, { once: true });
} else {
  getOrCreateProductMediaGridHandler();
}

// Also handle dynamically added product media
const observer = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    if (mutation.addedNodes.length) {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) { // Element node
          if (node.tagName === 'FLICKITY-CAROUSEL' || node.querySelector?.('flickity-carousel')) {
            const carousel = node.tagName === 'FLICKITY-CAROUSEL' ? node : node.querySelector('flickity-carousel');
            const productMedia = carousel?.closest('product-media');
            if (carousel && productMedia) {
              const handler = getOrCreateProductMediaGridHandler();
              handler.handleCarousel(carousel, productMedia);
            }
          }
        }
      });
    }
  });
});

observer.observe(document.body, {
  childList: true,
  subtree: true
});

// Ensure singleton is available for other scripts
getOrCreateProductMediaGridHandler();
